// Contratos HTTP compartilhados entre API e web (IM-API-xxx).
import { z } from 'zod';
import type { InvitationDeliveryDto } from './mail.js';
import type { PlatformEligibility, SessionContext } from './platform.js';
import type { EvidenceNature } from './evidence.js';

export * from './evidence.js';
export * from './opportunityFacts.js';
export * from './opportunity.js';
export * from './decisionBrief.js';
export * from './opportunityRadar.js';
export * from './commercialLearning.js';

export const ROLES = ['OWNER', 'ADMIN', 'MEMBER'] as const;
export type Role = (typeof ROLES)[number];

export const SIGNAL_CATEGORIES = [
  'expansao', 'investimento', 'tecnologia', 'lideranca', 'contratacao', 'concorrencia', 'outro',
  // IM-EXT-001: vocabulário comercial do Ceres (espelho de `CATEGORIAS_DE_SINAL`, IM-MIG-005).
  'CRESCIMENTO', 'EXPANSAO_REGIONAL', 'NOVO_PRODUTO_OU_SERVICO', 'TRANSFORMACAO_DIGITAL', 'MUDANCA_TECNOLOGICA',
  'AUMENTO_DE_COMPLEXIDADE', 'QUALIDADE_E_REGRESSAO', 'PERFORMANCE', 'SEGURANCA', 'INTEGRACAO_E_API',
  'MUDANCA_DE_LIDERANCA', 'CONTRATACAO_E_EQUIPE', 'INVESTIMENTO', 'PARCERIA', 'AQUISICAO', 'MUDANCA_DE_POSICIONAMENTO',
  'RISCO_OU_PROBLEMA_PUBLICO', 'POSSIVEL_SUBSTITUICAO_DE_FORNECEDOR', 'CLIENTE_OU_CASO_PUBLICO',
  'REDUCAO_OU_REESTRUTURACAO', 'MUDANCA_REGULATORIA', 'PUBLICACAO_ESTRATEGICA', 'OUTRO_SINAL_RELEVANTE',
] as const;
export type SignalCategory = (typeof SIGNAL_CATEGORIES)[number];

/** IM-API-010 */
export const LoginRequest = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(512),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export interface OrganizationSummary {
  id: string;
  slug: string;
  name: string;
  role: Role;
  /** IM-EXT-011: finalidade da organização; DEMONSTRACAO e TECNICO ganham marcação visual permanente. */
  purpose?: 'REAL' | 'DEMONSTRACAO' | 'TECNICO';
  /** IM-ADR-020: organização suspensa pela administração da plataforma (acesso operacional bloqueado no servidor). */
  suspended?: boolean;
  /** SAAS-007: PENDENTE mantém o produto fechado e direciona à retomada da contratação. */
  commercialStatus?: 'LEGADO' | 'PENDENTE' | 'AVALIACAO' | 'ATIVA' | 'SUSPENSA' | 'CANCELADA' | 'EXPIRADA';
}

/** IM-API-012 */
export interface MeResponse {
  user: { id: string; email: string; name: string };
  activeOrganization: OrganizationSummary | null;
  organizations: OrganizationSummary[];
  /** IM-ADR-019: contexto ativo da sessão. PLATFORM = administração da plataforma (sem organização ativa). */
  context?: SessionContext;
  /** Papel da plataforma do próprio usuário, quando houver concessão viva (não dá acesso a nada no contexto TENANT). */
  platform?: PlatformEligibility | null;
}

/** IM-API-013 */
export const SwitchOrganizationRequest = z.object({ organizationId: z.string().uuid() });

// IM-XPR-006 (IM-ADR-017): a organização é o tenant. Convite = caminho para um colega entrar na MESMA organização
// (antes, cada usuário só conseguia criar a sua e os dados ficavam separados).
export const CreateInvitationRequest = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  role: z.enum(['ADMIN', 'MEMBER']).default('MEMBER'),
  /** INTELIMOVE-SAAS-004: com o e-mail transacional ativo, o convite é enviado ao destinatário (false = só o link). */
  sendEmail: z.boolean().default(true),
}).strict();
/** IM-API-385 (SAAS-004): gerar novo link e, opcionalmente, reenviar por e-mail (o link anterior deixa de valer). */
export const ReissueInvitationRequest = z.object({ sendEmail: z.boolean().default(false) }).strict();
export const AcceptInvitationRequest = z.union([
  z.object({ token: z.string().min(20).max(128) }).strict(),
  z.object({ invitationId: z.string().uuid() }).strict(),
]);
export interface InvitationDto { id: string; email: string; role: 'ADMIN' | 'MEMBER'; state: 'PENDENTE' | 'ACEITO' | 'REVOGADO' | 'EXPIRADO'; invitedBy: string | null; expiresAt: string; createdAt: string; acceptedAt: string | null;
  /** INTELIMOVE-SAAS-004: situação do último e-mail deste convite (null = não enviado por e-mail). */
  delivery?: InvitationDeliveryDto | null }
export interface PendingInvitationDto { id: string; organizationId: string; organizationName: string; role: 'ADMIN' | 'MEMBER'; invitedBy: string | null; expiresAt: string }

/** IM-API-014 */
export const PasswordResetRequest = z.object({ email: z.string().trim().toLowerCase().email().max(254) });
export const PasswordResetConfirm = z.object({ token: z.string().min(32).max(128), password: z.string().min(10).max(512) });

/** IM-API-020/021. `feed`: RSS/Atom ou página de publicações (transplante do Ceres CERES-058). */
export const SOURCE_SCHEDULES = ['MANUAL', 'DIARIA', 'SEMANAL', 'QUINZENAL', 'MENSAL'] as const;
export const PAGE_TYPES = ['HOME', 'PRODUTOS', 'SERVICOS', 'PRECOS', 'CLIENTES', 'CASES', 'VAGAS', 'IMPRENSA', 'BLOG', 'RI', 'EVENTOS', 'DOCUMENTACAO', 'OUTRA'] as const;
const sourceMeta = {
  priority: z.enum(['ALTA', 'MEDIA', 'BAIXA']).default('MEDIA'),
  notes: z.string().trim().max(2000).optional(),
  targetId: z.string().uuid().optional(),
  relatedOfferingIds: z.array(z.string().uuid()).max(20).default([]),
  relatedCompetitor: z.string().trim().max(200).optional(),
};
export type SourceSchedule = (typeof SOURCE_SCHEDULES)[number];
export const CreateSourceRequest = z.discriminatedUnion('kind', [
  z.object({
    name: z.string().trim().min(2).max(120),
    kind: z.literal('demo_fixture'),
    config: z.object({ feed: z.string().regex(/^[a-z0-9-]{2,40}$/) }),
  }),
  z.object({
    name: z.string().trim().min(2).max(120),
    kind: z.literal('feed'),
    url: z.string().trim().min(8).max(2000),
    accountName: z.string().trim().min(2).max(200).optional(),
    schedule: z.enum(SOURCE_SCHEDULES).default('DIARIA'),
  }),
  // IM-EXT-003: página monitorada, canal do YouTube e perfil público de rede social.
  z.object({
    name: z.string().trim().min(2).max(120), kind: z.literal('page'), url: z.string().trim().min(8).max(2000),
    accountName: z.string().trim().min(2).max(200).optional(), pageType: z.enum(PAGE_TYPES).default('OUTRA'),
    reason: z.string().trim().max(500).optional(), schedule: z.enum(SOURCE_SCHEDULES).default('SEMANAL'),
    renderMode: z.enum(['AUTO', 'HTTP', 'RENDERED']).default('AUTO'), ...sourceMeta,
  }).strict(),
  z.object({
    name: z.string().trim().min(2).max(120), kind: z.literal('youtube'), reference: z.string().trim().min(2).max(400),
    accountName: z.string().trim().min(2).max(200).optional(), schedule: z.enum(SOURCE_SCHEDULES).default('SEMANAL'), ...sourceMeta,
  }).strict(),
  z.object({
    name: z.string().trim().min(2).max(120), kind: z.literal('social'), url: z.string().trim().min(4).max(400),
    accountName: z.string().trim().min(2).max(200).optional(), ...sourceMeta,
  }).strict(),
]);
export type CreateSourceRequest = z.infer<typeof CreateSourceRequest>;

/** IM-API-025 */
export const UpdateSourceRequest = z.object({
  status: z.enum(['active', 'paused']).optional(),
  schedule: z.enum(SOURCE_SCHEDULES).optional(),
  accountName: z.string().trim().min(2).max(200).nullable().optional(),
  priority: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  reason: z.string().trim().max(500).nullable().optional(),
  pageType: z.enum(PAGE_TYPES).optional(),
  archived: z.boolean().optional(),
}).strict().refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo.');

export type SourceKind = 'demo_fixture' | 'rss' | 'atom' | 'publications_page' | 'page' | 'youtube' | 'social';
export interface SourceDto {
  id: string;
  name: string;
  kind: SourceKind;
  status: 'active' | 'paused';
  lastRunAt: string | null;
  createdAt: string;
  lastRun: SourceRunDto | null;
  signalCount: number;
  url: string | null;
  feedUrl: string | null;
  accountName: string | null;
  schedule: SourceSchedule;
  nextRunAt: string | null;
  consecutiveFailures: number;
  requiresManualAction: boolean;
  lastOutcome: 'SEM_NOVIDADES' | 'COM_NOVIDADES' | 'FALHA' | null;
  lastError: string | null;
  // IM-EXT-003
  pageType?: string | null; reason?: string | null; priority?: string; notes?: string | null; targetId?: string | null;
  network?: string | null; integrationState?: string | null; renderMode?: string; archivedAt?: string | null; relatedCompetitor?: string | null;
  changesCount?: number;
  // IM-EXT-011: classificação, origem e saúde da fonte.
  classification?: SourceClassification; origin?: SourceOrigin | null;
  lastSuccessAt?: string | null; lastPublicationAt?: string | null; failedRuns?: number; targetName?: string | null;
}

/** IM-EXT-003: descoberta de fontes no site e decisão sobre a sugestão. */
export const DiscoverSourcesRequest = z.object({ url: z.string().trim().min(4).max(2000), companyName: z.string().trim().max(200).optional(), targetId: z.string().uuid().optional() }).strict();
export const SuggestionDecisionRequest = z.object({
  decision: z.enum(['ACCEPT', 'REJECT']), url: z.string().trim().min(4).max(2000).optional(), name: z.string().trim().min(2).max(120).optional(),
  accountName: z.string().trim().min(2).max(200).optional(), schedule: z.enum(SOURCE_SCHEDULES).optional(),
}).strict();

/** IM-API-026: linha do tempo de publicações/evidências da fonte. */
export interface SourceEvidenceDto {
  id: string;
  title: string;
  url: string | null;
  nature: EvidenceNature;
  publishedAt: string | null;
  fetchedAt: string | null;
  signalId: string | null;
  noSignalReason: string | null;
}

export type RunStatus = 'queued' | 'running' | 'succeeded' | 'failed';
export interface SourceRunDto {
  id: string;
  sourceId: string;
  status: RunStatus;
  trigger?: 'MANUAL' | 'AGENDADA';
  outcome?: 'SEM_NOVIDADES' | 'COM_NOVIDADES' | 'FALHA' | null;
  itemsWithoutSignal?: number;
  itemsSeen: number;
  evidencesCreated: number;
  signalsCreated: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** Estados do sinal e decisões humanas (kernel transplantado do Ceres, IM-EXT-001). */
export const SIGNAL_STATUSES = ['NOVO', 'VISTO', 'CONFIRMADO', 'REJEITADO', 'ACOMPANHANDO', 'SUPERADO', 'PRECISA_REVISAO'] as const;
export type SignalStatus = (typeof SIGNAL_STATUSES)[number];
export const SIGNAL_DECISIONS = ['VISTO', 'CONFIRMAR', 'REJEITAR', 'ACOMPANHAR', 'MARCAR_SUPERADO', 'REAVALIAR', 'CONVERTER_EMPRESA_ALVO'] as const;
export type SignalDecision = (typeof SIGNAL_DECISIONS)[number];
/** IM-API-030 */
export const ListSignalsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().max(200).optional(),
  category: z.enum(SIGNAL_CATEGORIES).optional(),
  status: z.enum([...SIGNAL_STATUSES, 'ativos']).optional(),
  /** IM-EXT-011: sinais utilizáveis (com publicação verificável) ficam separados dos sem evidência verificável. */
  evidence: z.enum(['verificavel', 'sem_evidencia', 'todos']).default('verificavel'),
});

/** IM-EXT-011: rastreabilidade do sinal. */
export const SOURCE_CLASSIFICATIONS = ['REAL', 'DEMONSTRATIVA', 'TECNICA_TESTE', 'ORIGEM_NAO_COMPROVADA'] as const;
export type SourceClassification = (typeof SOURCE_CLASSIFICATIONS)[number];
export type SourceOrigin = 'MANUAL' | 'SUGESTAO_APROVADA' | 'EMPRESA_ALVO' | 'CONCORRENTE' | 'PAGINA_MONITORADA' | 'CONTEXTO_DA_EMPRESA';
export type SignalVerification = 'VERIFICAVEL' | 'SEM_EVIDENCIA_VERIFICAVEL' | 'ORIGEM_DEMONSTRATIVA' | 'ORIGEM_NAO_COMPROVADA';
export type SignalValidationStatus = 'PENDENTE' | 'CONFIRMADO' | 'REJEITADO' | 'EVIDENCIA_INSUFICIENTE';
export type SignalTreatmentStatus = 'NOVO' | 'ACOMPANHANDO' | 'CONVERTIDO_EMPRESA_ALVO' | 'CONVERTIDO_ACAO' | 'RESOLVIDO' | 'ARQUIVADO';
export type PublicationAvailability = 'NAO_VERIFICADA' | 'DISPONIVEL' | 'INDISPONIVEL';
export interface SignalScoreBreakdownDto {
  version: string; total: number; raw: number; highPriorityEligible: boolean; computedAt: string;
  components: Array<{ key: string; label: string; points: number; max: number; reason: string }>;
  caps: Array<{ rule: string; limit: number; reason: string }>;
}
export interface SignalSourceRefDto {
  id: string; name: string; kind: SourceKind; classification: SourceClassification; url: string | null; domain: string | null;
  origin: SourceOrigin | null; accountName: string | null;
}
export interface SignalPublicationDto {
  /** URL exata da publicação original (só http/https públicos). Null = sem evidência verificável. */
  url: string | null; domain: string | null; originalTitle: string; publishedAt: string | null; fetchedAt: string | null;
  availability: PublicationAvailability; checkedAt: string | null; availabilityDetail: string | null;
}

/** IM-API-033 */
export const SignalDecisionRequest = z.object({
  decision: z.enum(SIGNAL_DECISIONS),
  note: z.string().trim().max(500).optional(),
});

export interface SignalDto {
  id: string;
  accountName: string;
  category: SignalCategory;
  title: string;
  summary: string;
  score: number;
  sourceName: string;
  detectedAt: string;
  status: SignalStatus;
  evidence: { id: string; url: string | null; publishedAt: string | null };
  /** Leitura explicável (Ceres CERES-055/057): só existe quando a origem produziu uma. */
  reading: { conclusionNature: string | null; timing: string | null; confidence: string | null; commercialReason: string | null; toConfirm: string[]; limitations: string[]; validUntil: string | null } | null;
  // IM-EXT-011
  verification: SignalVerification;
  validationStatus: SignalValidationStatus;
  treatmentStatus: SignalTreatmentStatus;
  nature: EvidenceNature;
  source: SignalSourceRefDto;
  publication: SignalPublicationDto;
  /** Null = pontuação gravada antes da IM-EXT-011, sem composição registrada. */
  scoreBreakdown: SignalScoreBreakdownDto | null;
}

/** IM-API-032 */
export interface SignalDetailDto extends SignalDto {
  evidenceDetail: { id: string; title: string; summary: string; url: string | null; nature: EvidenceNature; state: string; origin: string; excerpt: string | null; publishedAt: string | null; fetchedAt: string | null };
  decisions: Array<{ id: string; decision: SignalDecision; fromStatus: SignalStatus; toStatus: SignalStatus; note: string | null; decidedBy: string | null; createdAt: string;
    fromValidation: SignalValidationStatus | null; toValidation: SignalValidationStatus | null; fromTreatment: SignalTreatmentStatus | null; toTreatment: SignalTreatmentStatus | null }>;
  /** Empresa-alvo do tenant com o mesmo nome (ou vinculada à fonte), quando existe. */
  relatedTarget: { id: string; name: string } | null;
}

/** IM-API-104: resultado da verificação de disponibilidade da publicação original. */
export interface PublicationCheckDto { availability: PublicationAvailability; checkedAt: string; detail: string; score: number }

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface SignalSummary {
  /** Sinais utilizáveis (publicação original verificável). */
  total: number;
  highPriority: number;
  byCategory: Partial<Record<SignalCategory, number>>;
  /** IM-EXT-011: sinais sem evidência verificável, mantidos à parte (não entram nos números acima). */
  withoutEvidence: number;
}

export interface ApiError {
  error: { code: string; message: string; correlationId?: string };
}

// ---------------------------------------------------------------------------------------------------------------
// INTELIMOVE-CTX-001 — contexto canônico da empresa contratante (IM-CTX-API-001..).
// ---------------------------------------------------------------------------------------------------------------
export const CONTEXT_STATUSES = ['DRAFT', 'ENRICHING', 'REVIEW_REQUIRED', 'READY', 'STALE', 'FAILED', 'ARCHIVED'] as const;
export type ContextStatus = (typeof CONTEXT_STATUSES)[number];
export const OFFERING_KINDS = ['PRODUCT', 'SERVICE', 'SOLUTION'] as const;
export const ITEM_STATUSES = ['SUGGESTED', 'CONFIRMED', 'ARCHIVED'] as const;
// IM-EXT-007: RELATED e DISCARDED (decisão humana sobre a sugestão da pesquisa do concorrente).
export const COMPETITOR_STATUSES = ['HYPOTHESIS', 'CONFIRMED', 'RELATED', 'DISCARDED', 'ARCHIVED'] as const;
export const CUSTOMER_TYPES = ['B2B', 'B2C', 'MISTO'] as const;
export const COMPANY_SIZES = ['MEI', 'MICRO', 'PEQUENA', 'MEDIA', 'GRANDE'] as const;
export const SENIORITIES = ['OPERACIONAL', 'COORDENACAO', 'GERENCIA', 'DIRETORIA', 'C_LEVEL', 'SOCIO'] as const;
export const BUYING_ROLES = ['DECISOR', 'INFLUENCIADOR', 'USUARIO', 'TECNICO', 'FINANCEIRO', 'CAMPEAO', 'OUTRO'] as const;
export const LEVELS = ['ALTA', 'MEDIA', 'BAIXA'] as const;
export const GEOGRAPHIC_SCOPES = ['LOCAL', 'REGIONAL', 'NACIONAL', 'INTERNACIONAL'] as const;
/** IM-EXT-005: seções do contexto que recebem achados do enriquecimento. */
export const ENRICHMENT_SECTIONS = ['overview', 'identity', 'offerings', 'markets', 'icps', 'personas', 'differentiators', 'competitors', 'goals'] as const;
export type EnrichmentSection = (typeof ENRICHMENT_SECTIONS)[number];
export const FINDING_NATURES = ['FATO', 'INDICIO', 'HIPOTESE', 'CONFLITO'] as const;
export const MERGE_STATES = ['EMPTY', 'SAME', 'DIFFERENT', 'CONFLICT', 'MANUAL_PROTECTED', 'STALE'] as const;
export type MergeState = (typeof MERGE_STATES)[number];
/** Campos da identidade raiz: só mudam pelo fluxo controlado depois da primeira confirmação (IM-CTX-REQ-020). */
export const IDENTITY_FIELDS = ['legalName', 'taxId', 'tradeName', 'primaryDomain', 'country'] as const;
export type IdentityField = (typeof IDENTITY_FIELDS)[number];

const txt = (max: number) => z.string().trim().max(max);
const optTxt = (max: number) => txt(max).nullable().optional();
const list = (maxItems = 30, maxLen = 300) => z.array(z.string().trim().min(1).max(maxLen)).max(maxItems);
const uuidList = z.array(z.string().uuid()).max(100);

/** IM-CTX-API-001: nova empresa contratante = novo tenant. */
export const CreateCompanyRequest = z.object({
  companyName: z.string().trim().min(2).max(120),
  website: z.string().trim().max(2000).optional(),
  startAnalysis: z.boolean().default(true),
}).strict();

/** IM-CTX-API-002: cadastro de conta (somente com PUBLIC_SIGNUP_ENABLED). */
export const SignupRequest = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(10).max(512),
}).strict();

/** IM-CTX-API-004: campos editáveis do perfil (identidade raiz incluída só enquanto não travada). */
export const UpdateProfileRequest = z.object({
  expectedRevision: z.number().int().positive().optional(),
  legalName: optTxt(200), tradeName: txt(200).min(2).optional(), taxId: optTxt(40), country: z.string().regex(/^[A-Z]{2}$/).nullable().optional(),
  primaryDomain: optTxt(253),
  shortDescription: optTxt(500), longDescription: optTxt(5000),
  region: optTxt(100), city: optTxt(120), language: txt(20).min(2).optional(), currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  timezone: txt(60).min(3).optional(), additionalDomains: list(10, 253).optional(),
  sector: optTxt(120), subsectors: list(15, 120).optional(), businessModel: optTxt(200),
  customerType: z.enum(CUSTOMER_TYPES).nullable().optional(), companySize: z.enum(COMPANY_SIZES).nullable().optional(),
  markets: list(30, 160).optional(), servedRegions: list(40, 120).optional(), salesChannels: list(15, 120).optional(),
  differentiators: list(20, 300).optional(), commercialGoals: list(20, 300).optional(), commercialConstraints: list(20, 300).optional(),
  // IM-EXT-005: dados cadastrais e de mercado descobertos pelo enriquecimento.
  foundedOn: optTxt(20), legalNature: optTxt(200), registrationStatus: optTxt(60), shareCapital: z.number().nonnegative().max(1e15).nullable().optional(),
  cnaePrimary: optTxt(300), cnaeSecondary: list(40, 300).optional(), employeeRange: optTxt(60), headquarters: optTxt(300),
  address: optTxt(400), postalCode: optTxt(12), phone: optTxt(40), email: optTxt(254), socialProfiles: list(15, 500).optional(),
  logoUrl: optTxt(1000), languages: list(10, 40).optional(), geographicScope: z.enum(GEOGRAPHIC_SCOPES).nullable().optional(),
}).strict();
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequest>;

/** IM-CTX-API-005: alteração controlada da identidade raiz (OWNER). */
export const IdentityChangeRequest = z.object({
  changes: z.object({
    legalName: optTxt(200), tradeName: txt(200).min(2).optional(), taxId: optTxt(40),
    primaryDomain: optTxt(253), country: z.string().regex(/^[A-Z]{2}$/).nullable().optional(),
  }).strict().refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo.'),
  justification: z.string().trim().min(20).max(1000),
}).strict();

/** IM-CTX-API-006 */
export const StartAnalysisRequest = z.object({
  website: z.string().trim().max(2000).optional(), force: z.boolean().default(false),
  /** IM-EXT-006: grava o site informado como domínio principal (card "Pesquisa da empresa" e "Alterar site"). */
  saveWebsite: z.boolean().default(false),
  /** IM-EXT-006: motivo registrado na execução e na auditoria (primeira pesquisa, reexecução, nova tentativa, site alterado). */
  trigger: z.enum(['FIRST', 'RERUN', 'RETRY', 'SITE_CHANGED']).optional(),
}).strict();

/** IM-CTX-API-009 */
export const FindingDecisionRequest = z.object({
  decision: z.enum(['ACCEPT', 'EDIT', 'REJECT']),
  value: z.unknown().optional(),
}).strict();

/** IM-EXT-005: decisão em lote — selecionados, uma seção inteira ou "tudo que for elegível". */
export const BulkDecisionRequest = z.object({
  decision: z.enum(['ACCEPT', 'REJECT']),
  findingIds: z.array(z.string().uuid()).max(300).default([]),
  items: z.array(z.object({ kind: z.enum(['offerings', 'icps', 'personas', 'competitors']), id: z.string().uuid() }).strict()).max(300).default([]),
  section: z.enum(ENRICHMENT_SECTIONS).optional(),
  eligibleOnly: z.boolean().default(true),
}).strict();
export type BulkDecisionRequest = z.infer<typeof BulkDecisionRequest>;

export const OfferingInput = z.object({
  name: txt(160).min(2), kind: z.enum(OFFERING_KINDS).default('SERVICE'), description: optTxt(3000),
  problemsSolved: list().default([]), benefits: list().default([]), targetAudience: optTxt(500), regions: list(40, 120).default([]),
  sortOrder: z.number().int().min(0).max(10000).default(0),
}).strict();
export const IcpInput = z.object({
  name: txt(160).min(2), isGeneral: z.boolean().default(false), description: optTxt(3000), offeringIds: uuidList.default([]),
  segments: list().default([]), companySizes: list(10, 60).default([]), locations: list().default([]), maturity: optTxt(300),
  technologies: list().default([]), pains: list().default([]), needs: list().default([]), triggers: list().default([]),
  blockers: list().default([]), positiveCriteria: list().default([]), negativeCriteria: list().default([]),
}).strict();
export const PersonaInput = z.object({
  name: txt(160).min(2), equivalentTitles: list().default([]), department: optTxt(120),
  seniority: z.enum(SENIORITIES).nullable().optional(), buyingRole: z.enum(BUYING_ROLES).nullable().optional(),
  influence: z.enum(LEVELS).nullable().optional(), pains: list().default([]), goals: list().default([]), objections: list().default([]),
  offeringIds: uuidList.default([]),
}).strict();
export const CompetitorInput = z.object({
  name: txt(160).min(2), website: optTxt(500), reason: optTxt(1000), notes: optTxt(2000),
  status: z.enum(['HYPOTHESIS', 'CONFIRMED']).default('HYPOTHESIS'), offeringIds: uuidList.default([]),
}).strict();
export type ContextEntityKind = 'offerings' | 'icps' | 'personas' | 'competitors';

/** IM-CTX-API-012 */
export const ConfirmContextRequest = z.object({
  // IM-XPR-005: motivo OPCIONAL — a revisão das alterações já registra o que muda; sem motivo, grava-se um texto padrão.
  reason: z.string().trim().max(500).optional(),
  expectedRevision: z.number().int().positive().optional(),
}).strict();

export interface CompanyProfileDto {
  id: string; status: ContextStatus; revision: number; confirmedRevision: number | null; currentVersion: number | null;
  identityLocked: boolean; pendingChanges: boolean;
  legalName: string | null; tradeName: string | null; shortDescription: string | null; longDescription: string | null;
  taxId: string | null; country: string | null; region: string | null; city: string | null; language: string; currency: string;
  timezone: string; primaryDomain: string | null; additionalDomains: string[]; sector: string | null; subsectors: string[];
  businessModel: string | null; customerType: (typeof CUSTOMER_TYPES)[number] | null; companySize: (typeof COMPANY_SIZES)[number] | null;
  markets: string[]; servedRegions: string[]; salesChannels: string[]; differentiators: string[]; commercialGoals: string[];
  commercialConstraints: string[]; confirmedAt: string | null; confirmedBy: string | null; lastReviewedAt: string | null; updatedAt: string;
  foundedOn: string | null; legalNature: string | null; registrationStatus: string | null; shareCapital: number | null; cnaePrimary: string | null;
  cnaeSecondary: string[]; employeeRange: string | null; headquarters: string | null; address: string | null; postalCode: string | null;
  phone: string | null; email: string | null; socialProfiles: string[]; logoUrl: string | null; languages: string[];
  geographicScope: (typeof GEOGRAPHIC_SCOPES)[number] | null;
  /** Origem do valor atual por campo (MANUAL = informado/confirmado por pessoa; protegido contra sobrescrita). */
  fieldOrigins: Record<string, { origin: 'MANUAL' | 'ENRICHMENT'; at: string; by?: string | null }>;
}

/** IM-EXT-006: `suggested` = há sugestão pendente que cumpre o item (encontrada, ainda não confirmada); `section` = aba onde ela está. */
export interface ReadinessItem { key: string; label: string; done: boolean; suggested?: boolean; section?: string }
/** `coveredBySuggestions`: os itens que faltam têm todos uma sugestão pendente — basta revisar e confirmar. */
export interface ReadinessDto { ready: boolean; done: number; total: number; items: ReadinessItem[]; coveredBySuggestions?: boolean }

/** IM-EXT-006: linha do histórico do contexto (trilha de auditoria legível). */
export interface ContextActivityDto { id: string; action: string; at: string; actor: string | null; targetId: string | null; metadata: Record<string, unknown> }

export interface AnalysisRunDto {
  id: string; website: string; domain: string; status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'PARTIAL' | 'FAILED'; stage: string;
  pagesPlanned: number; pagesRead: number; pagesRefused: number; findingsCreated: number; analyzer: string;
  failureReason: string | null; notes: string[]; createdAt: string; startedAt: string | null; finishedAt: string | null;
  // IM-EXT-005
  companyName: string | null; force: boolean; sourcesDiscovered: number; sourcesCollected: number; sourcesFailed: number;
  identityResolution: 'EXACT' | 'HIGH_CONFIDENCE' | 'AMBIGUOUS' | 'NOT_FOUND' | null;
  identity: EnrichmentIdentityDto | Record<string, never>;
  llm: EnrichmentLlmDto | Record<string, never>;
  summary: EnrichmentSummaryDto | Record<string, never>;
  coverage: Record<string, SectionCoverageDto>;
  durationMs: number | null; confirmedAt: string | null;
}

export interface EnrichmentIdentityDto {
  resolution: 'EXACT' | 'HIGH_CONFIDENCE' | 'AMBIGUOUS' | 'NOT_FOUND';
  reason: string;
  chosen: IdentityCandidateDto | null;
  candidates: IdentityCandidateDto[];
  domains: { informed: string; final: string; alternatives: string[] };
}
export interface IdentityCandidateDto { cnpj: string; legalName: string; tradeName: string | null; city: string | null; uf: string | null; status: string | null; score: number; reasons: string[] }
export interface EnrichmentLlmDto {
  status: 'CALLED' | 'UNAVAILABLE' | 'FAILED' | 'SKIPPED'; provider: string | null; model: string | null; promptVersion: string | null; promptHash: string | null;
  calls: number; inputTokens: number; outputTokens: number; webSearches: number; costUsd: number | null; durationMs: number; reason: string | null;
  requiredVariable: string | null; discarded: number;
}
export interface EnrichmentSummaryDto {
  overview: { text: string; lacunas: string[] } | null;
  counts: { sources: number; pages: number; found: number; facts: number; indications: number; hypotheses: number; conflicts: number; notFound: number; sectionsReady: number; reinforced: number; suppressedByRejection: number };
}
export interface SectionCoverageDto {
  state: 'FOUND' | 'PARTIAL' | 'NOT_FOUND' | 'NOT_SEARCHED' | 'SOURCE_UNAVAILABLE' | 'CONFLICT';
  found: number; notFound: string[]; note: string | null;
}
export interface ContextSourceDto {
  id: string; runId: string; key: string; type: string; discoveredBy: string; originalUrl: string; finalUrl: string | null; title: string | null;
  role: string | null; status: string; httpStatus: number | null; failure: string | null; redirects: string[]; responseMs: number | null;
  contentHash: string | null; chars: number; injectionSuspected: boolean; fetchedAt: string;
}

export interface ContextEvidenceDto {
  id: string; url: string | null; title: string; excerpt: string | null; kind: string | null; nature: string; origin: string;
  confidence: string | null; confirmation: string | null; contentHash: string; collectedAt: string | null; runId: string | null; sourceId?: string | null;
}

export interface FindingDto {
  id: string; runId: string; field: string; value: unknown; nature: (typeof FINDING_NATURES)[number]; confidence: 'ALTA' | 'MEDIA' | 'BAIXA';
  status: 'PENDING' | 'ACCEPTED' | 'EDITED' | 'REJECTED' | 'SUPERSEDED'; appliedValue: unknown; evidence: ContextEvidenceDto | null;
  decidedAt: string | null; createdAt: string;
  // IM-EXT-005
  section: EnrichmentSection | null; mergeState: MergeState | null; claimOrigin: string | null; method: string | null; rationale: string | null;
  alternatives: Array<{ value: unknown; sources: string[] }>; currentValue: unknown; evidences: ContextEvidenceDto[]; eligible: boolean;
}

interface ItemMeta {
  id: string; status: string; origin: 'USER' | 'SITE_ANALYSIS' | 'ENRICHMENT'; version: number; evidence: ContextEvidenceDto | null; confirmedAt: string | null; confirmedBy: string | null; updatedAt: string;
  // IM-EXT-005 (presentes nos itens sugeridos pelo enriquecimento)
  runId?: string | null; nature?: string | null; confidence?: string | null; rationale?: string | null; claimOrigin?: string | null;
  details?: Record<string, unknown>; evidences?: ContextEvidenceDto[];
}
export type OfferingDto = ItemMeta & z.infer<typeof OfferingInput> & {
  /** IM-XPR-005: público no mapeamento das ofertas (nulo = ainda depende de ICP ligado) e segmentos próprios. */
  segments?: string[]; audienceScope?: 'TRANSVERSAL' | 'SEM_PUBLICO' | null; audienceNote?: string | null;
};
export type IcpDto = ItemMeta & z.infer<typeof IcpInput>;
export type PersonaDto = ItemMeta & z.infer<typeof PersonaInput>;
export type CompetitorDto = ItemMeta & Omit<z.infer<typeof CompetitorInput>, 'status'>;

export interface CompanyContextDto {
  profile: CompanyProfileDto;
  offerings: OfferingDto[]; icps: IcpDto[]; personas: PersonaDto[]; competitors: CompetitorDto[];
  readiness: ReadinessDto;
  latestRun: AnalysisRunDto | null;
  pendingFindings: number;
  /** IM-EXT-005: sugestões pendentes por seção (campos + itens sugeridos). */
  pendingBySection: Record<string, number>;
  subscription: { status: string; planCode: string | null; entitlements: Record<string, unknown> };
  /** IM-XPR-005: quantidade de diferenças SEMÂNTICAS em relação à versão confirmada em uso (0 = nada a confirmar). */
  semanticChanges?: number;
}

export interface ContextVersionDto { id: string; version: number; reason: string | null; snapshotHash: string; createdAt: string; createdBy: string | null; createdByName: string | null }
export interface ContextSnapshotDto extends ContextVersionDto { snapshot: ContextSnapshot }

/** Snapshot imutável — é o que a EXT-002 consome (IM-CTX-REQ-025). */
export interface ContextSnapshot {
  schema: 'intelimove.company-context/1';
  version: number;
  confirmedAt: string;
  profile: Omit<CompanyProfileDto, 'id' | 'status' | 'revision' | 'confirmedRevision' | 'currentVersion' | 'identityLocked' | 'pendingChanges' | 'confirmedAt' | 'confirmedBy' | 'lastReviewedAt' | 'updatedAt' | 'fieldOrigins'>;
  offerings: Array<Omit<OfferingDto, 'evidence' | 'confirmedBy' | 'updatedAt'> & { evidenceId: string | null }>;
  icps: Array<Omit<IcpDto, 'evidence' | 'confirmedBy' | 'updatedAt'> & { evidenceId: string | null }>;
  personas: Array<Omit<PersonaDto, 'evidence' | 'confirmedBy' | 'updatedAt'> & { evidenceId: string | null }>;
  competitors: Array<Omit<CompetitorDto, 'evidence' | 'confirmedBy' | 'updatedAt'> & { evidenceId: string | null }>;
}

/**
 * Contrato de entrada da próxima missão (INTELIMOVE-EXT-002). `strict`: um `tenantId`, nome ou CNPJ da empresa no
 * payload é rejeitado — a identidade da contratante vem do snapshot do tenant da sessão, nunca do cliente.
 */
export const MarketAnalysisContextRequest = z.object({
  contextSnapshotId: z.string().uuid().optional(),
  offeringIds: z.array(z.string().uuid()).min(1).max(20),
  objective: z.string().trim().min(10).max(1000),
  parameters: z.object({
    regions: z.array(z.string().trim().min(2).max(120)).max(20).optional(),
    segments: z.array(z.string().trim().min(2).max(160)).max(20).optional(),
    knownCompetitorIds: z.array(z.string().uuid()).max(30).optional(),
  }).strict().default({}),
}).strict();
export type MarketAnalysisContextRequest = z.infer<typeof MarketAnalysisContextRequest>;

// ---------------------------------------------------------------------------------------------------------------
// INTELIMOVE-EXT-002 — Inteligência de Mercado e Concorrentes (IM-MKT-API-001..).
// Todos `strict`: `tenantId`, nome/CNPJ da contratante ou snapshot arbitrário no payload são recusados (400).
// ---------------------------------------------------------------------------------------------------------------
export const MARKET_ANALYSIS_TYPES = ['COMPETITIVE_LANDSCAPE', 'OFFERING_COMPARISON', 'MARKET_ENTRY', 'POTENTIAL_MARKET', 'TARGET_DISCOVERY'] as const;
export const MARKET_STATUSES = ['DRAFT', 'QUEUED', 'RUNNING', 'NEEDS_REVIEW', 'COMPLETED', 'FAILED', 'CANCELED'] as const;
export type MarketStatus = (typeof MARKET_STATUSES)[number];
export const MARKET_RUN_STATUSES = ['QUEUED', 'RUNNING', 'COMPLETED', 'PARTIAL', 'FAILED', 'CANCELED'] as const;

const extraCompetitor = z.object({ name: z.string().trim().min(2).max(160), website: z.string().trim().max(400).optional() }).strict();

/** IM-MKT-API-002: rascunho. A contratante, o catálogo, ICPs e personas vêm do snapshot do tenant. */
export const CreateMarketAnalysisRequest = z.object({
  name: z.string().trim().min(3).max(160),
  objective: z.string().trim().min(10).max(1000),
  offeringIds: z.array(z.string().uuid()).min(1).max(20),
  analysisType: z.enum(MARKET_ANALYSIS_TYPES).default('COMPETITIVE_LANDSCAPE'),
  region: z.string().trim().max(200).optional(),
  aspects: z.array(z.string().trim().max(40)).max(20).optional(),
  discoverNew: z.boolean().default(true),
  knownCompetitorIds: z.array(z.string().uuid()).max(30).default([]),
  extraCompetitors: z.array(extraCompetitor).max(15).default([]),
  notes: z.string().trim().max(2000).optional(),
  contextSnapshotId: z.string().uuid().optional(),
  icpIds: z.array(z.string().uuid()).max(10).default([]),
  /** IM-EXT-009: concorrentes arquivados/descartados só entram quando a pessoa pede explicitamente. */
  includeArchived: z.boolean().default(false),
}).strict();
export type CreateMarketAnalysisRequest = z.infer<typeof CreateMarketAnalysisRequest>;

/** IM-MKT-API-004: parâmetros permitidos (somente sem execução em andamento). O snapshot não muda. */
export const UpdateMarketAnalysisRequest = z.object({
  name: z.string().trim().min(3).max(160).optional(),
  objective: z.string().trim().min(10).max(1000).optional(),
  offeringIds: z.array(z.string().uuid()).min(1).max(20).optional(),
  analysisType: z.enum(MARKET_ANALYSIS_TYPES).optional(),
  region: z.string().trim().max(200).nullable().optional(),
  aspects: z.array(z.string().trim().max(40)).max(20).optional(),
  discoverNew: z.boolean().optional(),
  knownCompetitorIds: z.array(z.string().uuid()).max(30).optional(),
  extraCompetitors: z.array(extraCompetitor).max(15).optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  includeArchived: z.boolean().optional(),
}).strict();

export const StartMarketRunRequest = z.object({ confirmUnderstanding: z.literal(true) }).strict();
export const ArchiveMarketAnalysisRequest = z.object({ confirm: z.literal(true), reason: z.string().trim().max(500).optional() }).strict();
/**
 * IM-EXT-009: decisões sobre concorrente de uma análise. PROMOTE_* = adicionar aos concorrentes conhecidos (cria ou liga
 * à relação canônica, sem duplicar); INVESTIGATE / DISMISS / RESET só mudam a fotografia desta análise. CONFIRM/REJECT
 * são os nomes antigos (equivalem a PROMOTE_CONFIRMED / DISMISS).
 */
export const CompetitorDecisionRequest = z.object({
  decision: z.enum(['PROMOTE_CONFIRMED', 'PROMOTE_HYPOTHESIS', 'INVESTIGATE', 'DISMISS', 'RESET', 'CONFIRM', 'REJECT']),
  note: z.string().trim().max(1000).optional(),
}).strict();
export const RecommendationDecisionRequest = z.object({ decision: z.enum(['ACEITA', 'DESCARTADA', 'PENDENTE']) }).strict();

// IM-EXT-010 — curadoria da SWOT. Mensagens de validação em linguagem de negócio (a tela as associa aos campos).
export const SWOT_QUADRANTS = ['STRENGTHS', 'WEAKNESSES', 'OPPORTUNITIES', 'THREATS'] as const;
export type SwotQuadrant = (typeof SWOT_QUADRANTS)[number];
const swotText = (max: number, label: string) => z.string().trim().max(max, `${label}: no máximo ${max} caracteres.`);
const swotFields = {
  quadrant: z.enum(SWOT_QUADRANTS, { message: 'Escolha Forças, Fraquezas, Oportunidades ou Ameaças.' }),
  title: z.string().trim().min(2, 'Informe um título com pelo menos 2 caracteres.').max(200, 'Título: no máximo 200 caracteres.'),
  description: swotText(4000, 'Descrição').nullable(),
  recommendedAction: swotText(1000, 'Ação recomendada').nullable(),
  impact: z.enum(['ALTA', 'MEDIA', 'BAIXA'], { message: 'Impacto deve ser alto, médio ou baixo.' }).nullable(),
  priority: z.number().int().min(1, 'Prioridade deve ser alta, média ou baixa.').max(3, 'Prioridade deve ser alta, média ou baixa.').nullable(),
  observation: swotText(1000, 'Observação').nullable(),
};
export const CreateSwotItemRequest = z.object({
  runId: z.string().uuid().optional(),
  quadrant: swotFields.quadrant,
  title: swotFields.title,
  description: swotFields.description.optional(),
  recommendedAction: swotFields.recommendedAction.optional(),
  impact: swotFields.impact.optional(),
  priority: swotFields.priority.optional(),
  observation: swotFields.observation.optional(),
}).strict();
export type CreateSwotItemRequest = z.infer<typeof CreateSwotItemRequest>;
export const UpdateSwotItemRequest = z.object({
  version: z.number().int().min(1),
  quadrant: swotFields.quadrant.optional(),
  title: swotFields.title.optional(),
  description: swotFields.description.optional(),
  recommendedAction: swotFields.recommendedAction.optional(),
  impact: swotFields.impact.optional(),
  priority: swotFields.priority.optional(),
  observation: swotFields.observation.optional(),
}).strict();
export type UpdateSwotItemRequest = z.infer<typeof UpdateSwotItemRequest>;
export const SwotItemStateRequest = z.object({ version: z.number().int().min(1), reason: z.string().trim().max(500).optional() }).strict();
export const ResolveSwotReviewRequest = z.object({
  version: z.number().int().min(1),
  decision: z.enum(['KEEP_CURATED', 'REPLACE', 'KEEP_BOTH', 'ARCHIVE_SUGGESTION']),
}).strict();

/** Natureza exibida: Fato, Indício, Hipótese — ou Recomendação (declarada como tal). */
export type MarketNature = 'FATO' | 'INDICIO' | 'HIPOTESE';

export interface MarketUnderstandingDto {
  company: string;
  summary: string;
  offerings: Array<{ id: string; name: string; kind: string }>;
  region: string;
  regionSource: string;
  icps: string[];
  personas: string[];
  differentiators: string[];
  knownCompetitors: string[];
  aspects: Array<{ key: string; label: string }>;
  questions: string[];
  discoverNew: boolean;
  contextVersion: number;
}

export interface MarketRunDto {
  id: string; runNo: number; trigger: string; status: (typeof MARKET_RUN_STATUSES)[number]; phase: string; step: string;
  stepLabel: string; executor: string; strategyVersion: string; contextVersion: number | null; attempts: number; maxAttempts: number;
  toolCalls: number; createdAt: string; startedAt: string | null; finishedAt: string | null; durationMs: number | null;
  heartbeatAt: string | null; cancelRequested: boolean; errorCode: string | null; errorSummary: string | null;
  canRetry: boolean; counts: { competitors: number; evidences: number; findings: number; recommendations: number };
}

export interface MarketAnalysisSummaryDto {
  id: string; name: string; objective: string; analysisType: string; region: string | null; status: MarketStatus;
  visibleState: string; archived: boolean; archivedAt: string | null; offerings: string[]; contextVersion: number;
  createdAt: string; lastRunAt: string | null; runsCount: number; summary: string | null; competitorsFound: number;
}

export interface MarketAnalysisDetailDto extends MarketAnalysisSummaryDto {
  aspects: string[]; discoverNew: boolean; includeArchived: boolean; notes: string | null; offeringIds: string[]; knownCompetitorIds: string[];
  extraCompetitors: Array<{ name: string; website?: string }>; contextSnapshotId: string; currentContextVersion: number | null;
  contextChangedSince: boolean; understanding: MarketUnderstandingDto; understandingConfirmedAt: string | null;
  activeRun: MarketRunDto | null; lastRun: MarketRunDto | null; editable: boolean;
  executor: { available: boolean; code: string | null; message: string | null };
  entitlement: { allowed: boolean; reason: string | null; usedToday: number; limitPerDay: number | null };
}

// ── IM-EXT-007 — busca assistida e enriquecimento automático de concorrentes ─────────────────────────────────────────
/** Estado humano do concorrente (COMPETITOR_STATUSES). A sugestão da pesquisa fica em `suggestedClassification` até alguém decidir. */
export type CompetitorStatus = (typeof COMPETITOR_STATUSES)[number];
export type CompetitorClassification = 'COMPETITOR' | 'RELATED' | 'NOT_COMPETITOR';
/** Campos do concorrente com procedência própria (informado, encontrado, inferido; confirmado, pendente, rejeitado). */
export const COMPETITOR_FIELDS = ['name', 'tradeName', 'legalName', 'website', 'taxId', 'city', 'region', 'country', 'description', 'reason', 'offeringIds', 'aliases'] as const;
export type CompetitorField = (typeof COMPETITOR_FIELDS)[number];
export interface CompetitorFieldOrigin { origin: 'USER' | 'AUTO' | 'AI'; state: 'CONFIRMED' | 'PENDING' | 'REJECTED'; at: string; by?: string | null; runId?: string | null }

export const CompetitorLookupQuery = z.object({ q: z.string().trim().min(3, 'Digite pelo menos 3 letras.').max(120), retry: z.enum(['1', '0']).optional() });
export interface CompetitorExistingDto { id: string; name: string; status: string; matchedBy: 'DOMAIN' | 'TAX_ID' | 'LEGAL_NAME' | 'NAME' }
export interface CompetitorCandidateDto {
  /** Identificador do candidato nesta busca (não é dado do cliente: o servidor reconfere tudo na seleção). */
  key: string;
  tradeName: string | null; legalName: string | null; domain: string | null; website: string | null;
  city: string | null; region: string | null; country: string | null; description: string | null; segment: string | null;
  taxId: string | null; registrationStatus: string | null;
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA'; signals: string[]; divergences: string[];
  sources: Array<{ kind: 'BUSCA' | 'REGISTRO' | 'SITE'; url: string | null; title: string }>;
  existing: CompetitorExistingDto | null;
  /** IM-EXT-008-C01: papel do candidato na lista (a ordem é explicada; nada é descartado só por ser rebaixado). */
  category?: CandidateCategory; categoryReason?: string | null; score?: number;
}
/**
 * PRINCIPAL: melhor hipótese operacional (só quando há confiança); POSSIVEL: candidata sem confirmação; RELACIONADA: mesmo
 * nome, outra pessoa jurídica; GRUPO: holding/participações; ESTRANGEIRA: registrada no exterior ou site de outro país;
 * IRREGULAR: baixada, inapta, suspensa ou nula; SITE_NAO_CONFIRMADO: subdomínio de terceiro, parceiro, demonstração ou marca parcial.
 */
export type CandidateCategory = 'PRINCIPAL' | 'POSSIVEL' | 'RELACIONADA' | 'GRUPO' | 'ESTRANGEIRA' | 'IRREGULAR' | 'SITE_NAO_CONFIRMADO';
/** COMPLETE: todas as fontes responderam; PARTIAL: alguma falhou (resultado pode estar incompleto); UNAVAILABLE: busca pública fora do ar. */
export interface CompetitorLookupDto { query: string; items: CompetitorCandidateDto[]; ambiguous: boolean; limitation: string | null; status: 'COMPLETE' | 'PARTIAL' | 'UNAVAILABLE'; tookMs: number; cached: boolean }

/** Seleção de um candidato (ou "Não encontrei a empresa": só nome e, se souber, o site). CNPJ nunca é obrigatório. */
export const CompetitorSelectionRequest = z.object({
  name: txt(160).min(2, 'Informe o nome da empresa.'),
  website: txt(500).nullable().optional(),
  taxId: z.string().trim().max(20).nullable().optional(),
  legalName: txt(300).nullable().optional(),
  manual: z.boolean().default(false),
}).strict();
export interface CompetitorIdentitySignalDto { key: string; label: string; state: 'CONFIRMA' | 'DIVERGE' | 'SEM_DADO'; detail: string }
export interface CompetitorIdentityDto {
  name: string; tradeName: string | null; legalName: string | null; aliases: string[]; domain: string | null; website: string | null;
  taxId: string | null; city: string | null; region: string | null; country: string | null; description: string | null;
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA'; signals: CompetitorIdentitySignalDto[]; divergences: string[];
  sources: Array<{ kind: 'BUSCA' | 'REGISTRO' | 'SITE'; url: string | null; title: string; fetchedAt: string }>;
  verifiedAt: string; existing: CompetitorExistingDto | null; note: string | null;
  /** IM-EXT-008-C01: outras pessoas jurídicas ligadas à marca (CNPJ publicado no site etc.), nunca misturadas à selecionada. */
  relatedEntities?: LegalEntityDto[];
}

// IM-EXT-008-C01 — entidades jurídicas de uma marca: a selecionada é a identidade inicial; CNPJs achados depois são
// classificados e guardados à parte, para decisão humana (nunca substituem a selecionada em silêncio).
export const LEGAL_RELATIONS = ['SELECIONADA', 'MATRIZ', 'FILIAL', 'GRUPO', 'FINANCEIRA', 'SEM_VINCULO', 'DIVERGENTE'] as const;
export type LegalRelation = (typeof LEGAL_RELATIONS)[number];
/** PRINCIPAL: a entidade acompanhada; RELACIONADA: marcada como relacionada; PENDENTE: aguarda decisão; DESCARTADA: não acompanhar. */
export type LegalEntityRole = 'PRINCIPAL' | 'RELACIONADA' | 'PENDENTE' | 'DESCARTADA';
export interface LegalEntityDto {
  cnpj: string; legalName: string | null; tradeName: string | null; status: string | null; city: string | null; uf: string | null;
  relation: LegalRelation; relationLabel: string; role: LegalEntityRole;
  domain: string | null; evidence: string | null; sourceUrl: string | null; foundAt: string;
  decidedBy?: string | null; decidedAt?: string | null;
}
export const LegalEntityDecisionRequest = z.object({
  cnpj: z.string().trim().regex(/^\d{14}$/, 'CNPJ com 14 dígitos.'),
  action: z.enum(['KEEP_SELECTED', 'MAKE_PRINCIPAL', 'MARK_RELATED', 'LEAVE_PENDING']),
}).strict();

export const CompetitorRerunRequest = z.object({ force: z.boolean().default(false), trigger: z.enum(['RERUN', 'RETRY']).default('RERUN') }).strict();
export const CompetitorClassifyRequest = z.object({ status: z.enum(COMPETITOR_STATUSES), note: txt(1000).optional() }).strict();
export const CompetitorFieldDecisionRequest = z.object({ field: z.enum(COMPETITOR_FIELDS), decision: z.enum(['CONFIRM', 'REJECT', 'ACCEPT_PROPOSAL', 'REJECT_PROPOSAL']) }).strict();
export const CompetitorEditRequest = z.object({
  name: txt(160).min(2).optional(), tradeName: optTxt(160), legalName: optTxt(300), website: optTxt(500), city: optTxt(120), region: optTxt(60),
  country: optTxt(60), description: optTxt(3000), reason: optTxt(1000), notes: optTxt(2000), aliases: list(10, 160).optional(), offeringIds: uuidList.optional(),
}).strict();

export type CompetitorRunStage = 'SELECTED' | 'PREPARING' | 'COLLECTING' | 'ANALYZING' | 'COMPARING' | 'READY_FOR_REVIEW' | 'PARTIAL' | 'FAILED';
export interface CompetitorRunDto {
  id: string; competitorId: string; version: number; status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'PARTIAL' | 'FAILED'; stage: CompetitorRunStage;
  trigger: 'FIRST' | 'RERUN' | 'RETRY' | 'SWITCH'; website: string | null; domain: string | null; companyName: string; force: boolean;
  requestedBy: string | null; requestedByName: string | null; sourcesDiscovered: number; sourcesCollected: number; sourcesFailed: number;
  llm: Partial<EnrichmentLlmDto>; notes: string[]; failureReason: string | null; durationMs: number | null;
  createdAt: string; startedAt: string | null; finishedAt: string | null;
  changes: { applied: string[]; proposed: string[]; preserved: string[] } | null;
}
/** Um item do resultado com natureza, confiança e evidências (ids em `evidences` do detalhe). */
export interface CompetitorAnalysisItem { value: string; note?: string | null; nature: 'FATO' | 'INDICIO' | 'HIPOTESE'; confidence: 'ALTA' | 'MEDIA' | 'BAIXA'; evidenceIds: string[] }
export type OverlapLevel = 'ALTA' | 'MEDIA' | 'BAIXA' | 'NENHUMA' | 'DESCONHECIDA';
export interface CompetitorAnalysisDto {
  generatedBy: 'IA' | 'REGRAS'; runId: string; version: number; analyzedAt: string;
  summary: string | null; suggestedClassification: CompetitorClassification | null; competitionLevel: 'ALTO' | 'MEDIO' | 'BAIXO' | 'NENHUM' | null;
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA'; rationale: string | null;
  overlap: Record<'customers' | 'problems' | 'offerings' | 'markets' | 'decisionMakers' | 'buyingMoments', { level: OverlapLevel; note: string | null; evidenceIds: string[] }>;
  offerings: Array<CompetitorAnalysisItem & { relation: 'MESMA' | 'SEMELHANTE' | 'ADJACENTE' | 'SEM_RELACAO'; tenantOfferings: string[] }>;
  segments: CompetitorAnalysisItem[]; regions: CompetitorAnalysisItem[]; audience: CompetitorAnalysisItem[]; apparentIcps: CompetitorAnalysisItem[];
  positioning: CompetitorAnalysisItem[]; valueProposition: CompetitorAnalysisItem[]; differentiators: CompetitorAnalysisItem[];
  clients: CompetitorAnalysisItem[]; partners: CompetitorAnalysisItem[]; technologies: CompetitorAnalysisItem[]; topics: CompetitorAnalysisItem[];
  socialProfiles: CompetitorAnalysisItem[]; news: CompetitorAnalysisItem[];
  favorable: CompetitorAnalysisItem[]; against: CompetitorAnalysisItem[]; gaps: string[];
  comparedWith: { contextVersion: number | null; offerings: string[]; confirmedOnly: boolean };
}
export interface CompetitorDetailDto {
  id: string; status: CompetitorStatus | 'SUPERSEDED'; origin: string; version: number;
  name: string; tradeName: string | null; legalName: string | null; aliases: string[]; domain: string | null; website: string | null;
  taxId: string | null; city: string | null; region: string | null; country: string | null; description: string | null;
  reason: string | null; notes: string | null; offeringIds: string[];
  identity: Partial<CompetitorIdentityDto>; identityConfidence: 'ALTA' | 'MEDIA' | 'BAIXA' | null;
  fieldOrigins: Partial<Record<CompetitorField, CompetitorFieldOrigin>>;
  suggestedClassification: CompetitorClassification | null; analysis: CompetitorAnalysisDto | null;
  reviewPending: boolean; reviewedAt: string | null; confirmedAt: string | null; createdAt: string; updatedAt: string;
  latestRun: CompetitorRunDto | null;
  /** IM-EXT-009: análise de mercado de onde a empresa foi incorporada (origem MARKET_ANALYSIS) e data do arquivamento. */
  sourceAnalysisId?: string | null; archivedAt?: string | null;
}
export * from './integrations.js';
export * from './battlecards.js';
export * from './segmentation.js';
export * from './sizing.js';
export * from './environment.js';
export * from './strategic.js';
export * from './recommendation.js';
export * from './sources.js';
export * from './portfolio.js';
export * from './executive.js';
export * from './scenarios.js';
export * from './learning.js';
export * from './journeys.js';
export * from './experience.js';
export * from './contextDiff.js';
export * from './offeringMap.js';
export * from './monitorState.js';
export * from './battlecardView.js';
export * from './targetDossier.js';
export * from './planning.js';
export * from './platform.js';
export * from './platformOps.js';
export * from './tenantAdmin.js';
export * from './mail.js';
export * from './commercial.js';
export * from './billing.js';
export * from './onboarding.js';
